import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import { Explain } from './Explain';
import './css/trust.css';

// Settings — for now, MCP access tokens (DECISIONS.md 2026-07-07): bearer
// tokens that let your own LLM client (Claude Desktop / Claude Code) use
// Worldview as a research tool. The token is shown ONCE at mint; only its
// hash is stored. The MCP surface can never vote — it scaffolds inert
// structure; you activate it here in the app.

export default function Settings() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState('');
  const [minted, setMinted] = useState(null); // { token, name } — shown once
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    apiFetch('/api/auth/mcp-token')
      .then((r) => (r.ok ? r.json() : []))
      .then(setTokens)
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const mint = async (e) => {
    e.preventDefault();
    setError(null);
    setCopied(false);
    try {
      const res = await apiFetch('/api/auth/mcp-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'mcp client' }),
      });
      if (!res.ok) throw new Error();
      setMinted(await res.json());
      setName('');
      load();
    } catch {
      setError('Failed to mint a token');
    }
  };

  const revoke = async (id) => {
    try {
      await apiFetch(`/api/auth/mcp-token/${id}`, { method: 'DELETE' });
      if (minted && tokens.find((t) => t.id === id)?.created_at === minted.created_at) setMinted(null);
      load();
    } catch {
      setError('Failed to revoke');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(minted.token);
      setCopied(true);
    } catch {}
  };

  return (
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h1 style={{ marginTop: 0 }}>Settings</h1>
        <p className="trust-muted" style={{ fontSize: '13px' }}>
          Signed in as <strong>{user?.username}</strong>
        </p>

        <h2 style={{ fontSize: '17px', marginTop: '28px' }}>
          <Explain concept="mcp-access">MCP access</Explain>
        </h2>
        <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
          Setup instructions live in <code style={{ fontSize: '13px' }}>mcp/README.md</code>.
        </p>
        <p className="trust-muted" style={{ fontSize: '13px' }}>
          <Explain concept="personal-access-token">A token acts as you.</Explain>
        </p>

        {error && <div style={{ color: 'var(--wv-neg)', marginBottom: '10px' }}>{error}</div>}

        {minted && (
          <div className="wv-panel" style={{ marginBottom: '16px', borderColor: 'var(--wv-pos)' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '6px' }}>
              Token for “{minted.name}” — copy it now, it will not be shown again
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: '12px', wordBreak: 'break-all', background: 'var(--wv-surface2)', padding: '6px 8px', borderRadius: '6px', flex: 1, minWidth: '260px' }}>
                {minted.token}
              </code>
              <button onClick={copy} style={{ fontSize: '12px' }}>{copied ? '✓ copied' : 'Copy'}</button>
            </div>
          </div>
        )}

        <form onSubmit={mint} style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Token name (e.g. claude-desktop)…"
            style={{ flex: 1, minWidth: '220px' }}
          />
          <button type="submit">Mint token</button>
        </form>

        {tokens.length === 0 ? (
          <p className="trust-muted" style={{ fontSize: '13px' }}>No tokens yet.</p>
        ) : (
          tokens.map((t) => (
            <div key={t.id} className="wv-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '14px' }}>{t.name || 'unnamed'}</strong>
              <span className="trust-muted" style={{ fontSize: '12px' }}>
                  created {new Date(t.created_at).toLocaleDateString()} ·{' '}
                {t.last_used_at ? `last used ${new Date(t.last_used_at).toLocaleString()}` : 'never used'}
              </span>
              <button onClick={() => revoke(t.id)} style={{ marginLeft: 'auto', fontSize: '12px' }}>
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
